/**
 * useUndoRedoHotkeys.ts
 * Keyboard shortcut hook for Skill Matrix undo/redo.
 *
 * Binds global keydown listener for:
 *   Ctrl+Z        → undo (safeUndo)
 *   Ctrl+Shift+Z  → redo (safeRedo)
 *   Ctrl+Y        → redo (safeRedo — Windows convention)
 *   ⌘+Z           → undo (macOS)
 *   ⌘+Shift+Z     → redo (macOS)
 *
 * Skips the shortcut when focus is inside an INPUT, TEXTAREA, or any
 * contentEditable element so the browser's own edit undo still works
 * in those contexts (e.g. the employee search box).
 *
 * On API failure the store function re-throws; we catch here and pass
 * the error message to the optional `onError` callback so the page can
 * show a toast without coupling the hook to a specific notification
 * library.
 */

import { useEffect } from 'react';
import { safeUndo, safeRedo } from '../../../../state/skillMatrixStore';

interface UseUndoRedoHotkeysOptions {
  /** Called when safeUndo or safeRedo throws (e.g. network failure). */
  onError?: (message: string) => void;
  /**
   * When false, the undo/redo shortcuts are inert. The Skill Matrix is
   * view-only until the user enables Edit mode, and you cannot undo edits
   * that the read-only guard prevents (BUG-2026-0098). Defaults to true.
   */
  enabled?: boolean;
}

export function useUndoRedoHotkeys({ onError, enabled = true }: UseUndoRedoHotkeysOptions = {}): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = async (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;

      // Skip shortcut when the user is typing in a form element.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      const isUndo = e.key === 'z' && !e.shiftKey;
      const isRedo = (e.key === 'z' && e.shiftKey) || e.key === 'y';

      if (!isUndo && !isRedo) return;

      e.preventDefault();

      try {
        if (isUndo) {
          await safeUndo();
        } else {
          await safeRedo();
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to sync undo/redo with server';
        onError?.(msg);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onError, enabled]);
}
